import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { putAndValidate } from "~/shared/services/api/fetch-client.ts";
import { pickEndpoint } from "~/shared/services/api/pick-endpoint.ts";
import { extractApiError } from "~/shared/utils/extract-api-error.ts";

import type { UserPreferences } from "../schemas/user-preferences-schema.ts";
import { userPreferencesSchema } from "../schemas/user-preferences-schema.ts";
import { USER_PREFERENCES_QUERY_KEY } from "./use-get-user-preferences.ts";

export const usePutUserPreferences = () => {
	const { showError } = useNotificationActions();
	const queryClient = useQueryClient();
	const { isInternal } = useDeploymentType();
	const { isVendor } = useIsVendor();

	const url = pickEndpoint(
		{
			internal: apiRoutes.USERPREFERENCES,
			external: apiRoutes.USERPREFERENCES,
			vendor: apiRoutes.VENDORUSERPREFERENCES,
		},
		{ isInternal, isVendor },
	);

	return useMutation({
		mutationFn: async (body: UserPreferences) => {
			// User column preferences are only persisted for external users.
			if (isInternal) {
				return body;
			}
			const result = await putAndValidate(url, body, userPreferencesSchema);
			// `putAndValidate` reports HTTP and schema-validation failures as
			// `{ success: false }` rather than throwing. Convert those to a thrown
			// error so the mutation transitions to `onError` — otherwise a failed
			// save would silently look successful and the optimistic cache would
			// drift from what was actually persisted.
			if (!result.success) {
				throw new Error(result.error || "Failed to save grid preferences.");
			}
			return result.data;
		},
		onError: async (error) => {
			await queryClient.invalidateQueries({
				queryKey: USER_PREFERENCES_QUERY_KEY,
			});
			const apiError = await extractApiError(error);
			const message =
				apiError?.error ??
				(error instanceof Error && error.message ? error.message : null) ??
				"Failed to save grid preferences.";
			showError(message);
		},
	});
};
